import React, { useEffect, useState }  from 'react'
import { FormBag, FormData, FormUpdateEvent, createFormBag } from 'react-formage';

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { StopCreate } from 'domains/org/getty/components/stops/stop-create'
import { GettyStore, GettyAppState, StopWithSpace } from 'domains/org/getty/store'
import { getSearchFilterValues, saveSearchFilterValue } from 'domains/org/getty/utils/search-filter'

import { spacesBindableActionCreators } from 'domains/spaces/actions'
import { SpacesStore } from 'domains/spaces/store'
import { ValidationFailuresList } from 'domains/org/getty/components/publishing/validationsList'
import { ErrorsList } from 'domains/org/getty/components/publishing/errorsList'

import { getSubnav } from '../subnav'

import { StopUpdateTours } from './stop-update-tours/index'
import { ListItem } from './list-item'

import { Button, ButtonGroup } from 'components/ui/button'
import { IconPlus } from 'components/ui/icons';
import { Input } from 'components/ui/input'
import { usePagination, Paginator } from 'components/pagination'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { SectionHeader } from 'components/ui/section-header'
import { SelectFilter, SelectFilterItem } from 'components/ui/select-filter'
import { StatusGuard } from 'components/ui/status-guard'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { connect } from 'containers/store'
import { SharedActionCreators, sharedActionCreators } from 'containers/shared'

import { Page } from 'generated/mos/pagination'
import { Placement, SpacePlacement } from 'generated/mos/placement'
import { Space, Site } from 'generated/mos/structure'
import { Stop } from 'generated/mos/org/getty/audio'
import { ValidationFailure } from 'generated/mos/org/getty/validation'
import { ListStopsRequest } from 'generated/mos/org/getty/audioaccess'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'

import { bffEndpoint } from 'helpers/bff'
import { pick, pluralise, isNotNullOrUndefined } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'

import styled from 'styled'

import { Search } from './search'
type ListItemProps = {
  projectId: string;
  historyPush: (id: string) => void;
  stop: Stop.Entity;
}

const FilterButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -${space(6)};
  margin-bottom: ${space(3)};
`;

type FormValues = {
  searchText: string;
};


const SearchButton = styled(Button)`
  color: ${({ theme }) => theme.color.gray};
  margin-right: ${space(4)};
`

const SearchFilterContainer = styled.div`
  display: flex;
  align-items: center;
`

const SearchFilterItemWrapper = styled.div`
  margin-right: ${space(4)};
`

const SearchInputContainer = styled.div`
    margin-top: -10px;
    margin-right: ${space(4)};
    max-height: ${space(15)}
`

type ActionProps = Pick<GettyActionCreators, 'gettyListStopsRequest' | 'gettyDeleteStopReset' | 'gettyListValidationFailuresRequest' | 'gettyListSitesRequest' | 'gettyListStopsSearchRequest'> &
  Pick<typeof spacesBindableActionCreators, "spacesRequest"> &
  Pick<SharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyAppState, 'stops' | 'stopDelete' | 'validationFailuresList' | 'sites'> & Pick<SpacesStore["spaces"], "spacesList">;
type DirectProps = {
  projectId: string;
  historyPush: (id: string) => void;
  readonly navBackRoute?: string;
};
type Props = ActionProps & ConnectedProps & DirectProps;

const StopsPageView = (props: Props) => {
  const {
    gettyDeleteStopReset,
    gettyListStopsRequest,
    gettyListStopsSearchRequest,
    historyPush,
    projectId,
    navBackRoute,
    stopDelete,
    toastNotification,
    spacesRequest,
    spacesList,
    sites,
    gettyListSitesRequest,
    validationFailuresList,
    gettyListValidationFailuresRequest,
  } = props

  const [modalOpen, setModalOpen] = useState(false);
  const [ formBag, setFormBag ] = useState<FormBag<FormValues>>(createFormBag({ searchText: '' }))
  const [ searchTimeout, setSearchTimeout ] = useState<any>(undefined)

  const data = statusSelector.data(props.stops)
  const validationFailuresListData = statusSelector.data(validationFailuresList)
  const spacesListData = statusSelector.data(spacesList)
  const sitesData = statusSelector.data(sites)

  const hostName = bffEndpoint(window.location.hostname)

  // check if values being read and stored in session storage are valid
  // ignore values if not in the BooleanFlagValues set
  const validateValues = (newValue: any) => {
    const keys = Object.getOwnPropertyNames(newValue)
    keys.forEach((key) => {
      if (key === 'inTour' || key === 'hasIssues' || key === 'hasLocation' || key === 'hasRecentUpdates') {
        ListStopsRequest.ListStopFilter.BooleanFlagValues.has(newValue[key]) ?
          newValue :
          newValue[key] = undefined
      }
    })

    return newValue
  }

  const storedFilterValues = sessionStorage.length && getSearchFilterValues(validateValues)
  const sessionSiteId = storedFilterValues.allStopsLocation

  const [ storedSearch, setStoredSearch ] = useState(storedFilterValues.allStopsSearchText)

  const selectedSite =
    sites.status === Status.Ready &&
    sites.data.sites &&
    sites.data.sites.filter(site => Site.mustRef(site.ref).id === sessionSiteId)[0]

  const pagination = usePagination({ pageSize: 50 });

  const resolvePlacement = (placement: Placement.Entity) => {
    const resolved = spacesListData && spacesListData.find((item: Space.Entity) => {
      const id = Space.mustRef(item.ref).id
      return placement &&
        placement.type === Placement.refName &&
        placement.placement &&
        placement.placement.type === SpacePlacement.refName &&
        placement.placement.spaceRef &&
        id === placement.placement.spaceRef.id
    })
    const site = resolved && resolved.siteRef && sitesData && sitesData.sites && sitesData.sites.find(({ ref }: Site.Entity) => resolved.siteRef && Site.mustRef(ref).id === resolved.siteRef.id)
    return {
      placement: resolved ? resolved.displayName || resolved.name : undefined,
      site: site ? site.name : undefined
    }
  }

  const resolveLocationFilterLabel = () => {
    const hasLocationValue = storedFilterValues.hasLocation
    if (hasLocationValue === 'BOOLEAN_FLAG_FALSE') {
      return 'No Location'
    }

    if (selectedSite) {
      return selectedSite.name
    }

    return 'All Locations'
  }

  const resolveMoreFiltersLabel = () => {
    const inTourValue = storedFilterValues.inTour
    const hasIssuesValue = storedFilterValues.hasIssues
    const hasRecentUpdatesValue = storedFilterValues.hasRecentUpdates

    if (inTourValue === 'BOOLEAN_FLAG_TRUE') {
      return 'In a tour'
    }
    else if (inTourValue === 'BOOLEAN_FLAG_FALSE') {
      return 'Not in a tour'
    }

    if (hasIssuesValue === 'BOOLEAN_FLAG_TRUE') {
      return 'Has Issues'
    }

    if (hasRecentUpdatesValue === 'BOOLEAN_FLAG_TRUE') {
      return 'Has updates in the last 24 hours'
    }

    return 'All Stops'
  }

  const resolveBundleFiltersLabel = () => {
    const byInABundleValue = storedFilterValues.byInABundle
    const byIncludedInBundle = storedFilterValues.byIncludedInBundle

    if (byInABundleValue === 'BOOLEAN_FLAG_FALSE') {
      return 'Not marked for inclusion'
    }

    if (byInABundleValue === 'BOOLEAN_FLAG_TRUE') {
      return 'Included in a bundle'
    }

    if (byIncludedInBundle && byIncludedInBundle.includes('BUNDLE_TYPE_PRIVATE')) {
      return 'Included in private bundle'
    }

    if (byIncludedInBundle && byIncludedInBundle.includes('BUNDLE_TYPE_PUBLIC')) {
      return 'Included in public bundle'
    }

    return 'All Statuses'
  }

  // wrapper function to call the action creator using filter values
  const updateListStopsRequest = (newValues?: any) => {
    // use current filter values if no new values are passed
    const updatedValues = newValues ? newValues : storedFilterValues

    gettyListStopsRequest({
      ...ListStopsRequest.defaults,
      page: {
        ...Page.defaults,
        skipItems: pagination.skipItems,
        pageSize: pagination.pageSize,
      },
      sortOrder: 'SORT_MODIFIED_DESC',
      filter: {
        ...ListStopsRequest.ListStopFilter.defaults,
        bySites: updatedValues.allStopsLocation ? [{
          typename: Site.refName,
          id: updatedValues.allStopsLocation}] : [],
        byInTour: updatedValues.inTour ? updatedValues.inTour : 'BOOLEAN_FLAG_IGNORE',
        searchText: storedSearch,
        byHasLocation: updatedValues.hasLocation ? updatedValues.hasLocation: 'BOOLEAN_FLAG_IGNORE',
        byHasIssues: updatedValues.hasIssues ? updatedValues.hasIssues : 'BOOLEAN_FLAG_IGNORE',
        byRecentlyUpdated: updatedValues.hasRecentUpdates ? updatedValues.hasRecentUpdates : 'BOOLEAN_FLAG_IGNORE',
        byIncludedInBundle: updatedValues.byIncludedInBundle ? updatedValues.byIncludedInBundle : [],
        byInABundle: updatedValues.byInABundle ? updatedValues.byInABundle : 'BOOLEAN_FLAG_IGNORE',
      }
    })
  }

  // wrapper function to call the action creator used when searching
  const updateListStopsSearchRequest = () => {
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }

    setSearchTimeout(setTimeout(() => {
      gettyListStopsSearchRequest({
        ...ListStopsRequest.defaults,
        page: {
          ...Page.defaults,
          skipItems: pagination.skipItems,
          pageSize: pagination.pageSize,
        },
        sortOrder: 'SORT_MODIFIED_DESC',
        filter: {
          ...ListStopsRequest.ListStopFilter.defaults,
          bySites: storedFilterValues.allStopsLocation ? [{
            typename: Site.refName,
            id: storedFilterValues.allStopsLocation}] : [],
          byInTour: storedFilterValues.inTour ? storedFilterValues.inTour : 'BOOLEAN_FLAG_IGNORE',
          searchText: storedSearch,
          byHasLocation: storedFilterValues.hasLocation ? storedFilterValues.hasLocation: 'BOOLEAN_FLAG_IGNORE',
          byHasIssues: storedFilterValues.hasIssues ? storedFilterValues.hasIssues : 'BOOLEAN_FLAG_IGNORE',
          byRecentlyUpdated: storedFilterValues.hasRecentUpdates ? storedFilterValues.hasRecentUpdates : 'BOOLEAN_FLAG_IGNORE',
          byIncludedInBundle: storedFilterValues.byIncludedInBundle ? storedFilterValues.byIncludedInBundle : [],
          byInABundle: storedFilterValues.byInABundle ? storedFilterValues.byInABundle : 'BOOLEAN_FLAG_IGNORE',
        }
      })
    }, 300))
  }

  // {{{ keep page data up to date

  useEffect(() => {
    if(sites.status === Status.Ready && sites.data.sites) {
      updateListStopsRequest()
    }
  }, [sites.status, sites]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    gettyListSitesRequest()
    formBag.values.searchText = storedFilterValues.allStopsSearchText
  }, [gettyListSitesRequest]) // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (stopDelete.status === Status.Complete) {
      toastNotification({
        type: 'success',
        text: 'Stop deleted successfully',
      })
      gettyDeleteStopReset()
    }

    if (spacesList.status !== Status.Ready){
      spacesRequest()
    }
    gettyListSitesRequest()
    updateListStopsRequest()

    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: pagination.pageSize,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
      }
    })


  }, [gettyListStopsRequest, projectId, pagination.skipItems, pagination.pageSize, toastNotification, stopDelete, spacesRequest, gettyListSitesRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.stops.status === Status.Ready) {
      const entityRefs = stops.map(({ ref }) => ref).filter(isNotNullOrUndefined)
      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: pagination.pageSize,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs,
        }
      })
    }
  }, [gettyListValidationFailuresRequest, props.stops]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.stops.status === Status.Ready) {
      updateListStopsSearchRequest()
    }
  }, [storedSearch]) // eslint-disable-line react-hooks/exhaustive-deps


  // {{{ keep totalItems up to date (it's not initially present, and may change if data is added)
  const totalItems = data ? data.totalItems : undefined;

  useEffect(() => {
    totalItems !== undefined && pagination.setTotalItems(totalItems);
  }, [totalItems, pagination]);
  // }}}

  const stops = data ? data.entityList : [];
  const stopsCount = data ? data.totalItems : 0;

  const allLocationsOption = {
    label: 'All Locations',
    value: undefined
  }

  const siteOpts = sites.status === Status.Ready && sites.data.sites ?
    sites.data.sites.map((site: Site.Entity) => ({
      label: site.name,
      value: site,
    })) : []

  // add an all locations option to sites
  const allOpts = [allLocationsOption, ...siteOpts]

  const hasAtLeastOneOverride = (stop: any): boolean => {
    return 'hasAtLeastOneOverride' in stop ? stop.hasAtLeastOneOverride === true : false;
  }

  const onFormUpdate = (event: FormUpdateEvent<FormValues>) => {
    setFormBag(event.bag)
    const searchTerm = event.bag.values.searchText ? event.bag.values.searchText.trim() : ''

    if (storedSearch !== searchTerm) {
      saveSearchFilterValue({allStopsSearchText: searchTerm}, validateValues )
      setStoredSearch(searchTerm)
    }
  }

  const OuterListItem = ({stop}: ListItemProps) => {
    const stopId = Stop.mustRef(stop.ref).id
    const resolvedLocations = stop.placement && resolvePlacement(stop.placement.value as Placement.Entity)

    const [stopUpdatedInModal, setStopUpdatedInModal] = useState<StopWithSpace>();

    return (
      <>
        {stopUpdatedInModal && (
        <StopUpdateTours
          stop={stopUpdatedInModal}
          onCancel={() => setStopUpdatedInModal(undefined)}
          onComplete={() => {
            setStopUpdatedInModal(undefined)
            updateListStopsRequest()
          }}
        />
      )}
        <ListItem
          key={stopId}
          stop={stop}
          placement={resolvedLocations && resolvedLocations.placement}
          site={resolvedLocations && resolvedLocations.site}
          historyPush={historyPush}
          projectId={projectId}
          addToTour={() => setStopUpdatedInModal(stop)}
          hasWarning={validationFailuresListData && !!validationFailuresListData.find(({ ref }: ValidationFailure.Entity) => ref && ref.id === stopId)}
          stopsListItemLink
          hostName={hostName}
          hasAtLeastOneOverride={hasAtLeastOneOverride(stop)}
      />
      </>
    )
  }

  return (
    <>
      {modalOpen && (
        <StopCreate
          onComplete={stop => {
            const id = Stop.mustRef(stop.ref).id
            props.historyPush(`/project/${projectId}/stop/${id}`)
          }}
          onCancel={() => setModalOpen(false)}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit Project"
            navBackRoute={navBackRoute}
            darkTheme
          />
        )}
        title="GettyGuide"
        toolbar={() => (
          <ButtonGroup>
            <ValidationFailuresList projectId={projectId} />
            <Button appearance="primary" onClick={() => props.historyPush(`/project/${projectId}/publishing`)}>
              Publish
            </Button>
          </ButtonGroup>
        )}
        subnav={() => (
          <>
            <ErrorsList projectId={projectId} />
            <SecondaryNavigation navItems={getSubnav(projectId)}>
              <Button variant="subtle" onClick={() => setModalOpen(true)}>
                <IconPlus size="small" />
                Create Stop
              </Button>
            </SecondaryNavigation>
          </>
        )}
        content={() => <Search projectId={projectId} historyPush={historyPush} listItem={OuterListItem} />}
      />
    </>
  )
};

export const StopsPage = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, 'stops', 'stopDelete', 'validationFailuresList', 'sites'),
    ...pick(store.spaces, 'spacesList'),
  }),
  {
    ...pick(actionCreators, 'gettyListStopsRequest', 'gettyDeleteStopReset', 'gettyListValidationFailuresRequest', 'gettyListSitesRequest', 'gettyListStopsSearchRequest'),
    ...pick(spacesBindableActionCreators, "spacesRequest"),
    ...pick(sharedActionCreators, 'toastNotification')
  }
)(StopsPageView)
